import type { Destination } from "@/shared/contracts/destinations";

/** Onde a pessoa está quando pergunta — o contrato de FIO, e por isso aqui e não
 *  no composable que o produz. Nada entra sem estar no `runTurnSchema`, que é
 *  estrito. `title` é o nome que a PESSOA vê no menu, não o da rota. */
export interface PageContext {
	path: string;
	title: string;
	/** O código da entidade dinâmica, em `/e/:entityCode`. */
	entity?: string;
	recordId?: string;
	contactId?: string;
	conversationId?: string;
	/** A versão do site aberta em `/site` (`?site=<id>`). */
	siteId?: string;
	/** O agente aberto em `/agents/:code` — o Ben lê o conhecimento próprio dele. */
	agentCode?: string;
}

export interface StreamInput {
	agent: string;
	prompt: string;
	sessionId?: string | null;
	history?: Array<{ role: "user" | "assistant"; content: string }>;
	recordId?: string | null;
	/**
	 * A customer conversation this turn is ABOUT — the door it came in through.
	 *
	 * Not the same as `sessionId`, and both travel together here: the session is
	 * the operator's own thread, the conversation is what they are asking about.
	 * The server puts the turn on that conversation's trail and still files any
	 * question the agent raises against the SESSION, where the operator is sitting.
	 */
	conversationId?: string | null;
	/**
	 * Images in this turn. NATIVE input rather than a tool call (ADR-007): the
	 * model sees the picture the way a person would, instead of a description of
	 * it written by something that saw it first.
	 */
	images?: Array<{ mediaType: string; data: string }>;
	/**
	 * A nota de voz do compositor. O servidor a transcreve na borda
	 * (`promptWithAudio`) e o turno segue como texto — o modelo de chat não ouve.
	 */
	audio?: { mediaType: string; data: string };
	/**
	 * Vídeo, áudio, PDF, planilha — já guardados (`POST /v3/files/upload`), por
	 * id. O servidor lê-os na borda: quadros, transcrição, texto.
	 */
	files?: Array<{ id: string }>;
	/** A tela em que a pergunta foi feita. Ver `usePageContext()`. */
	page?: PageContext;
	/** Para onde dá para ir. Viaja no turno porque o servidor não tem esta lista:
	 *  `nav-items.ts` é do front e as entidades são do tenant — uma segunda lista
	 *  no back ficaria velha na primeira tela criada. */
	destinations?: readonly Destination[];
}

/**
 * O corpo do turno, no formato exato que o servidor aceita.
 *
 * Separado do `stream.ts` porque são duas coisas: aquele arquivo é o TRANSPORTE
 * (fetch, blocos SSE, aborto) e este é a FORMA do pedido. Quem mexer num não
 * precisa ler o outro, e o arquivo do fio voltou para dentro do teto de linhas.
 *
 * ── POR QUE OS LIMITES ESTÃO AQUI E NÃO SÓ NO SERVIDOR ────────────────────
 *
 * O `runTurnSchema` é ESTRITO e cada campo tem um `max()`: o que não cabe não é
 * ignorado, derruba a pergunta inteira com um 400. E dois destes limites são
 * alcançáveis com dados normais de um tenant — o nome de uma entidade pode ter
 * 255 caracteres (`structure.dto.ts:207`) e nada impede um workspace de ter mais
 * de 80 entidades ativas. Sem o corte aqui, o workspace grande é justamente o que
 * veria TODO turno do painel morrer, e o sintoma na tela seria a doca calada.
 * Cortar um rótulo custa três reticências; deixar passar custa o Ben inteiro.
 */
const MAX_DESTINATIONS = 80;
const MAX_LABEL = 80;
const MAX_TITLE = 200;

function clamp(text: string, max: number): string {
	return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

/** Chave ausente e não `undefined`: um campo que o schema não declara derruba o
 *  pedido inteiro, e `undefined` no `JSON.stringify` some — mas `null` não. */
export function bodyOf(input: StreamInput): Record<string, unknown> {
	// O registro na tela É o registro de que se fala. Nenhum lugar do front mandava
	// `recordId`, e o servidor já o usava para escopar a memória e achar a etapa do
	// funil. Um explícito ganha da rota.
	const recordId = input.recordId ?? input.page?.recordId;
	return {
		agent: input.agent,
		prompt: input.prompt,
		...(input.sessionId ? { sessionId: input.sessionId } : {}),
		...(input.history?.length ? { history: input.history } : {}),
		...(recordId ? { recordId } : {}),
		...(input.conversationId ? { conversationId: input.conversationId } : {}),
		...(input.images?.length ? { images: input.images } : {}),
		...(input.audio ? { audio: input.audio } : {}),
		...(input.files?.length ? { files: input.files.map((f) => ({ id: f.id })) } : {}),
		...(input.page ? { page: { ...input.page, title: clamp(input.page.title, MAX_TITLE) } } : {}),
		// Só `to` e `label`: o item é `strictObject` no servidor, e a dica junto
		// derrubaria o pedido inteiro em vez de ser ignorada.
		...(input.destinations?.length
			? {
					destinations: input.destinations
						.slice(0, MAX_DESTINATIONS)
						.map((d) => ({ to: d.to, label: clamp(d.label, MAX_LABEL) })),
				}
			: {}),
	};
}
